import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { api } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n } from 'shared/i18n';
import { PermissionsResponse, AddPermissionInput, RemovePermissionInput } from './types';

export const permissionQueryKeys = {
  permission: 'PERMISSION',
  documentPermissions: 'document-permissions',
} as const;

export function useGetPermissions(documentId: string | undefined): UseQueryResult<PermissionsResponse> {
  return useQuery({
    queryKey: [permissionQueryKeys.permission, documentId],

    queryFn: async () => {
      const res = await api.GET('/documents/{superId}/permissions', { params: { path: { superId: Number(documentId) } } });
      return res.data;
    },

    enabled: !!documentId,
  });
}

export function useAddPermissionToUser(): UseMutationResult<void, unknown, AddPermissionInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, permission, userId }: AddPermissionInput) => {
      await api.POST('/documents/{superId}/permissions/{permission}/users', {
        params: { path: { superId: Number(documentId), permission } },
        body: { userId },
      });
    },

    onError: (error) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('document-permissions:add-user.error'),
        removeTime: 4000,
      });
      ServerErrorHandler(error);
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('document-permissions:add-user.success.title'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [permissionQueryKeys.permission, variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: [permissionQueryKeys.documentPermissions, variables.documentId],
      });
    },
  });
}

export function useRemovePermissionFromUser(): UseMutationResult<void, unknown, RemovePermissionInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, permission, userId }: RemovePermissionInput) => {
      await api.DELETE('/documents/{superId}/permissions/{permission}/users/{userId}', {
        params: { path: { superId: Number(documentId), permission, userId: Number(userId) } },
      });
    },

    onError: (error) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('document-permissions:remove-user.error'),
        removeTime: 4000,
      });
      ServerErrorHandler(error);
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('document-permissions:remove-user.success.title'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [permissionQueryKeys.permission, variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: [permissionQueryKeys.documentPermissions, variables.documentId],
      });
    },
  });
}
